import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import CustomSkeleton from '../../Skeleton/CustomSkeleton';
import {
  convertToCurrency,
  convertToDollarLabel,
  convertToCurrencyWithoutSign,
  convertToCurrencyWithPercent,
  convertToPercentageWithDecimal,
  convertToRoundOffWithNumberOfDecimals,
  convertToRoundOffWithNumberOfDecimalsWithPercent
} from '../../../utils/Utils';
import { chartConfig } from '../../../utils/Config';
import {
  DOLLAR_FORMAT_COLUMNS,
  COMMA_FORMAT_COLUMNS,
  COMMA_FORMAT_COLUMNS_WITH_PERCENT,
  ZERO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT,
  ONE_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT,
  TWO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT,
  ZERO_DECIMAL_FORMAT_COLUMNS,
  ONE_DECIMAL_FORMAT_COLUMNS,
  TWO_DECIMAL_FORMAT_COLUMNS,
  TWO_DECIMAL_COMMA_FORMAT_COLUMNS_WITH_PERCENT,
  ONE_DECIMAL_COMMA_FORMAT_COLUMNS_WITH_PERCENT
} from '../../../utils/Constants';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const BarChart = ({ chartData, title, valueLabel, id, isBarChartLoading }) => {
  const totalLabels = chartData?.labels?.length || 0;
  const [range, setRange] = useState([0, totalLabels]);
  const [dragging, setDragging] = useState(null);
  const sliderRef = useRef(null);
  if (chartData && chartData.datasets && chartData.datasets.length > 0) {
    const dataPairs = chartData.labels?.map((label, index) => ({
      label,
      currentYearData: chartData.datasets[0].data[index],
      previousYearData: chartData.datasets[1].data[index]
    }));

    dataPairs?.sort((a, b) => b.currentYearData - a.currentYearData);

    chartData.labels = dataPairs?.map((pair) => pair.label);
    chartData.datasets[0].data = dataPairs?.map((pair) => pair.currentYearData);
    chartData.datasets[1].data = dataPairs?.map((pair) => pair.previousYearData);
  }
  useEffect(() => {
    if (totalLabels > 0 && (range[1] !== totalLabels || range[0] !== 0)) {
      setRange([0, totalLabels]);
    }
  }, [totalLabels]);
  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!dragging) return;

      const sliderRect = sliderRef.current.getBoundingClientRect();
      const sliderWidth = sliderRect.width;
      const stepSize = sliderWidth / totalLabels;
      const offsetX = event.clientX - sliderRect.left;
      const currentIndex = Math.round(offsetX / stepSize);

      if (dragging === 'range') {
        const rangeWidth = range[1] - range[0];
        const newRange = [
          currentIndex - Math.floor(rangeWidth / 2),
          currentIndex + Math.ceil(rangeWidth / 2)
        ];

        if (newRange[0] >= 0 && newRange[1] <= totalLabels) {
          setRange(newRange);
        }
      } else if (dragging === 'start') {
        if (currentIndex >= 0 && currentIndex < range[1]) {
          setRange([currentIndex, range[1]]);
        }
      } else if (dragging === 'end') {
        if (currentIndex > range[0] && currentIndex <= totalLabels) {
          setRange([range[0], currentIndex]);
        }
      }
    };

    const handleMouseUp = () => {
      setDragging(null);
    };

    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, range, totalLabels]);

  const handleMouseDown = (type) => {
    setDragging(type);
  };

  const filteredData = {
    labels: chartData?.labels?.slice(range[0], range[1]),
    datasets: [
      {
        label: 'Current Year',
        data: chartData?.datasets?.[0]?.data?.slice(range[0], range[1]) || [],
        backgroundColor: '#B05788',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
        borderRadius: 10
      },
      {
        label: 'Previous Year',
        data: chartData?.datasets?.[1]?.data?.slice(range[0], range[1]) || [],
        backgroundColor: '#33ABDA',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1,
        borderRadius: 10
      }
    ]
  };

  const options = {
    interaction: chartConfig,
    responsive: true,
    animation: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            if (valueLabel === 'Profitpercentage' || valueLabel === 'Wastepercentage') {
              return value + '%';
            }
            return `$ ${Number(value)?.toLocaleString()}`;
          }
        }
      },
      datalabels: { display: false }
    },
    scales: {
      x: {
        ticks: {
          callback: function (value, index) {
            const visibleLabels = filteredData.labels;
            const isSpaceAvailable = visibleLabels.length <= 7;

            if (isSpaceAvailable) {
              return visibleLabels[index];
            } else {
              const label = visibleLabels[index];
              return label && label.length > 7 ? label?.slice(0, 7) + '...' : label;
            }
          }
        }
      },
      y: {
        ticks: {
          callback: function (value) {
            if (['Revenue', 'RevenuePerSku', 'Profit']?.indexOf(valueLabel) > -1) {
              return convertToDollarLabel(value);
            }
            if (DOLLAR_FORMAT_COLUMNS.indexOf(valueLabel) > -1) {
              return convertToCurrency(value);
            }
            if (COMMA_FORMAT_COLUMNS.indexOf(valueLabel) > -1) {
              return convertToCurrencyWithoutSign(value);
            }
            if (COMMA_FORMAT_COLUMNS_WITH_PERCENT.indexOf(valueLabel) > -1) {
              return convertToCurrencyWithPercent(value);
            }
            if (ZERO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT.indexOf(valueLabel) > -1) {
              return convertToPercentageWithDecimal(value, 0);
            }
            if (ONE_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT.indexOf(valueLabel) > -1) {
              return convertToPercentageWithDecimal(value, 1);
            }
            if (TWO_DECIMAL_FORMAT_COLUMNS_WITH_PERCENT.indexOf(valueLabel) > -1) {
              return convertToPercentageWithDecimal(value, 2);
            }
            if (ZERO_DECIMAL_FORMAT_COLUMNS.indexOf(valueLabel) > -1) {
              return convertToRoundOffWithNumberOfDecimals(value, 0);
            }
            if (ONE_DECIMAL_FORMAT_COLUMNS.indexOf(valueLabel) > -1) {
              return convertToRoundOffWithNumberOfDecimals(value, 1);
            }
            if (TWO_DECIMAL_FORMAT_COLUMNS.indexOf(valueLabel) > -1) {
              return convertToRoundOffWithNumberOfDecimals(value, 2);
            }
            if (TWO_DECIMAL_COMMA_FORMAT_COLUMNS_WITH_PERCENT.indexOf(valueLabel) > -1) {
              return convertToRoundOffWithNumberOfDecimalsWithPercent(value, 2);
            }
            if (ONE_DECIMAL_COMMA_FORMAT_COLUMNS_WITH_PERCENT.indexOf(valueLabel) > -1) {
              return convertToRoundOffWithNumberOfDecimalsWithPercent(value, 1);
            }
          }
        }
      }
    }
  };

  return isBarChartLoading ? (
    <Box
      data-testid="custom-skeleton"
      id={id}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 24px',
        borderRadius: '8px',
        border: '1px solid #DBEAFE',
        backgroundColor: '#F8FAFC',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '200px'
      }}>
      <CustomSkeleton numberOfSkeleton={5} />
    </Box>
  ) : (
    <Box
      id={id}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 24px',
        borderRadius: '8px',
        border: '1px solid #DBEAFE',
        backgroundColor: '#F8FAFC',
        width: '100%'
      }}>
      <Stack justifyContent={'space-between'} direction={'row'} gap={2}>
        <Typography variant="overline" sx={{ fontWeight: 'bold', color: '#374151' }}>
          {title}
        </Typography>
      </Stack>

      <Box sx={{ marginTop: '16px', width: '100%' }}>
        <Bar data={filteredData} options={options} />
      </Box>
      {/* Custom Slider */}
      <Box
        data-testid="slider-container"
        ref={sliderRef}
        sx={{
          position: 'relative',
          margin: '20px 0',
          height: '20px',
          background: '#E5E7EB',
          borderRadius: '10px',
          cursor: dragging ? 'grabbing' : 'grab'
        }}>
        {/* Active range */}
        <Box
          sx={{
            position: 'absolute',
            height: '100%',
            background: '#B05788',
            left: `${(range[0] / totalLabels) * 100}%`,
            width: `${((range[1] - range[0]) / totalLabels) * 100}%`,
            borderRadius: '10px'
          }}
          onMouseDown={() => handleMouseDown('range')}
        />
        {/* Start handle */}
        <Box
          sx={{
            position: 'absolute',
            height: '20px',
            width: '20px',
            background: '#FFFFFF',
            border: '2px solid #B05788',
            borderRadius: '50%',
            top: '50%',
            left: `${(range[0] / totalLabels) * 100}%`,
            transform: 'translate(-50%, -50%)'
          }}
          onMouseDown={() => handleMouseDown('start')}
        />
        {/* End handle */}
        <Box
          data-testid="bar-chart"
          sx={{
            position: 'absolute',
            height: '20px',
            width: '20px',
            background: '#FFFFFF',
            border: '2px solid #B05788',
            borderRadius: '50%',
            top: '50%',
            left: `${(range[1] / totalLabels) * 100}%`,
            transform: 'translate(-50%, -50%)'
          }}
          onMouseDown={() => handleMouseDown('end')}
        />
      </Box>
    </Box>
  );
};

export default BarChart;
